import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Money, Handshake, Clock, ChatText, ArrowsClockwise } from "@phosphor-icons/react";

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
};

const RANGES = [
  { key: "week",  label: "This week",  days: 7 },
  { key: "month", label: "This month", days: 30 },
  { key: "qtr",   label: "This qtr",   days: 90 },
  { key: "year",  label: "Year to date", days: 365 },
];

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function TeamMemberPage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("timeline");

  const load = async (rangeKey) => {
    setBusy(true);
    const days = RANGES.find(r => r.key === rangeKey)?.days || 30;
    const end = new Date();
    const start = new Date(); start.setDate(end.getDate() - days);
    try {
      const { data } = await api.get(`/team/user/${userId}`, { params: {
        start_date: start.toISOString().slice(0, 10),
        end_date:   end.toISOString().slice(0, 10),
      }});
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load teammate detail");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(range); }, [range, userId]); // eslint-disable-line

  const openDeals = useMemo(() => (data?.deals || []).filter(d => !d.is_closed), [data]);
  const closedDeals = useMemo(() => (data?.deals || []).filter(d => d.is_closed), [data]);

  const initials = (data?.user?.name || "?").slice(0, 1).toUpperCase();

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="team-member-page">
      <button onClick={() => nav(-1)} data-testid="team-back" className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 mb-3">
        <ArrowLeft size={13}/>Back
      </button>

      {/* Header */}
      <section className="bg-white border border-slate-200 rounded-md p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="team-member-header">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-slate-900 text-white grid place-items-center text-xl font-bold" data-testid="team-member-avatar">{initials}</div>
          <div>
            <div className="label-micro">Sales · teammate</div>
            <h1 className="font-display text-2xl font-black text-slate-900" data-testid="team-member-name">{data?.user?.name || (busy ? "Loading…" : "Teammate")}</h1>
            {data?.user?.email && <div className="text-xs text-slate-500 mt-0.5">{data.user.email}{data.user.role ? ` · ${data.user.role}` : ""}</div>}
            {data?.pipeline_label && <div className="text-[10px] text-slate-400 mt-0.5">{data.pipeline_label} · {data.start_date} → {data.end_date}</div>}
          </div>
        </div>
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid="team-range">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRange(r.key)}
                    data-testid={`team-range-${r.key}`}
                    className={`text-xs font-semibold px-3 py-1.5 transition-colors ${range === r.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </section>

      {/* KPI grid */}
      <section className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="team-kpis">
        <Kpi icon={Handshake} tone="blue"    label="Open deals"       value={data?.stats?.deals_owned ?? 0}/>
        <Kpi icon={Money}     tone="slate"   label="Open pipeline"    value={fmtINR(data?.stats?.open_pipeline_value)}/>
        <Kpi icon={Trophy}    tone="emerald" label="Closed-won"       value={fmtINR(data?.stats?.closed_won_value)} sub={`${data?.stats?.closed_won_count || 0} wins`}/>
        <Kpi icon={Clock}     tone="amber"   label="Avg cycle"        value={data?.stats?.avg_cycle_days ? `${data.stats.avg_cycle_days}d` : "—"}/>
        <Kpi icon={ChatText}  tone="violet"  label="Notes logged"     value={data?.stats?.notes_this_period ?? 0}/>
        <Kpi icon={ArrowsClockwise} tone="rose" label="Deals moved"   value={data?.stats?.deals_moved_this_period ?? 0}/>
      </section>

      {/* Tabs */}
      <div className="mt-6 border-b border-slate-200 flex items-center gap-1">
        {[{ k: "timeline", label: "Activity timeline" }, { k: "open", label: `Open deals (${openDeals.length})` }, { k: "closed", label: `Closed (${closedDeals.length})` }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  data-testid={`team-tab-${t.k}`}
                  className={`text-xs font-semibold px-3 py-2 border-b-2 transition-colors ${tab === t.k ? "border-blue-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "timeline" && (
        <section className="mt-5" data-testid="team-timeline">
          {busy && !data && <div className="text-xs text-slate-500 italic text-center py-6">Loading timeline…</div>}
          {!busy && (data?.timeline || []).length === 0 && <div className="text-xs text-slate-500 italic text-center py-10 bg-white border border-slate-200 rounded-md">No activity in this window.</div>}
          <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4">
            {(data?.timeline || []).map((ev, i) => (
              <li key={i} className="ml-4 relative" data-testid={`team-timeline-item-${i}`}>
                <span className={`absolute -left-[22px] top-1 w-3.5 h-3.5 rounded-full border-2 border-white ${ev.kind === "note" ? "bg-violet-500" : "bg-rose-500"}`}/>
                <div className="bg-white border border-slate-200 rounded-sm p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs">
                      <span className="font-semibold text-slate-900">{ev.kind === "note" ? "Note on" : "Moved stage on"} </span>
                      <Link to={`/crm?deal=${ev.deal_hs_id}`} className="text-blue-600 hover:underline">{ev.deal_name}</Link>
                      {ev.amount > 0 && <span className="ml-2 text-[10px] text-slate-500">· {fmtINR(ev.amount)}</span>}
                    </div>
                    <span className="text-[10px] text-slate-400">{relTime(ev.at)}</span>
                  </div>
                  {ev.detail && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{ev.detail}</div>}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {(tab === "open" || tab === "closed") && (
        <section className="mt-5 bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`team-deals-${tab}`}>
          {busy && !data && <div className="text-xs text-slate-500 italic text-center py-10">Loading deals…</div>}
          {!busy && (tab === "open" ? openDeals : closedDeals).length === 0 && (
            <div className="text-xs text-slate-500 italic text-center py-10">No {tab === "open" ? "open" : "closed"} deals in this window.</div>
          )}
          {(tab === "open" ? openDeals : closedDeals).length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200">
                  <th className="label-micro py-2 px-3">Deal</th>
                  <th className="label-micro py-2 px-3">Stage</th>
                  <th className="label-micro py-2 px-3 text-right">Amount</th>
                  <th className="label-micro py-2 px-3">{tab === "open" ? "Last moved" : "Closed on"}</th>
                </tr>
              </thead>
              <tbody>
                {(tab === "open" ? openDeals : closedDeals).map(d => (
                  <tr key={d.hs_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`team-deal-row-${d.hs_id}`}>
                    <td className="py-2.5 px-3">
                      <Link to={`/crm?deal=${d.hs_id}`} className="font-semibold text-blue-700 hover:underline">{d.name}</Link>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${d.is_won ? "bg-emerald-100 text-emerald-700" : d.is_closed ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>{d.stage_label}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-semibold">{fmtINR(d.amount)}</td>
                    <td className="py-2.5 px-3 text-xs text-slate-500">{relTime(tab === "closed" ? (d.close_date || d.stage_updated_at) : d.stage_updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, tone, label, value, sub }) {
  const tones = {
    blue: "bg-blue-100 text-blue-700", slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700",
    violet: "bg-violet-100 text-violet-700", rose: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`team-kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-md grid place-items-center ${tones[tone] || tones.slate}`}><Icon size={14} weight="fill"/></div>
        <div className="label-micro">{label}</div>
      </div>
      <div className="font-display font-black text-xl text-slate-900">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
